import { useEffect, useState } from "react";
import styles from "./CollapsibleDateRange.module.scss";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import * as Yup from "yup";
import { Formik } from "formik";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";

interface Props {
  title: string;
  onChangeSelected: (fieldDate: string, from?: Date | null, to?: Date | null) => void;
  fieldDate?: string | null;
  fromDate?: Date | null;
  toDate?: Date | null;
  readonly: boolean;
}

const CollapsibleDateRange: React.FC<Props> = ({
  title,
  onChangeSelected,
  fieldDate,
  fromDate,
  toDate,
  readonly,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const [field, setField] = useState<string>(fieldDate ?? 'creationDate');
  const [from, setFrom] = useState<Date | null>(fromDate || null);
  const [to, setTo] = useState<Date | null>(toDate || null);  

  const filterSchema = Yup.object().shape({
    fromDate: Yup.date().nullable().optional(),
    toDate: Yup.date()
      .nullable()
      .optional()
      .when("fromDate", (fromDate, schema) =>
        fromDate
          ? schema.min(fromDate, "To Date must be greated than From Date")
          : schema.optional()
      ),
  });

  const toggleCollapsible = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    onChangeSelected(field, from, to);
  }, [field, from, to]);

  useEffect(() => {
    setField(fieldDate ?? 'creationDate');
    setFrom(fromDate || null);
    setTo(toDate || null);
  }, [fieldDate, fromDate, toDate]);

  return (
    <div className={styles.dataRange}>
      <div onClick={toggleCollapsible} className={styles.header}>
        <p className={styles.title}>{title}</p>
        <div className={styles.icon}>{isOpen ? <ExpandLess /> : <ExpandMore  />}</div>
      </div>
      {isOpen && (
        <div>
          <Formik
            initialValues={{ fromDate: from, toDate: to }}
            validationSchema={filterSchema}
            onSubmit={(values) => {}}
            validateOnChange
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
                <RadioGroup
                  aria-labelledby="fielDate-group-label"
                  name="fielDate"
                  className={styles.fieldDate}
                  row
                  value={field}
                  onChange={(event) => {
                    setField(event.currentTarget.value);
                  }}
                >
                  <FormControlLabel value={'creationDate'} control={<Radio />} label={t("creationDate")} />
                  <FormControlLabel value={'publicationDate'} control={<Radio />} label={t("publicationDate")} />
                </RadioGroup>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className={styles.picker}
                    disabled={readonly}
                    label={t("fromDate")}
                    value={from ? dayjs(from) : null}
                    onChange={(newValue: Dayjs | null) => {
                      var entryDate = newValue ? newValue.toDate() : undefined;
                      setFrom(entryDate !== undefined ? entryDate : null);
                      formik.setFieldValue("fromDate", entryDate || null);
                      setTimeout(() => {
                        formik.validateField("toDate");
                      }, 500);
                    }}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className={styles.picker}
                    label={t("toDate")}
                    disabled={readonly}
                    value={to ? dayjs(to) : null}
                    onChange={(newValue: Dayjs | null) => {
                      var entryDate = newValue ? newValue.toDate() : undefined;
                      setTo(entryDate !== undefined ? entryDate : null);
                      formik.setFieldValue("toDate", entryDate || null);
                      setTimeout(() => {
                        formik.validateField("toDate");
                      }, 500);
                    }}
                  />
                </LocalizationProvider>
                {formik.errors.toDate && (
                  <div className={styles.formikError}>
                    {formik.errors.toDate}
                  </div>
                )}
              </form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default CollapsibleDateRange;
